import React, { useEffect } from 'react';
import { Radio, Typography, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
    EMPTY_RESPONSES_EXCLUDE,
    EMPTY_RESPONSES_GENERATE_WITH_AI,
    EMPTY_RESPONSES_NONE_SELECTED,
    EMPTY_RESPONSES_UNLABELED,
    emptyResponseDefault,
    emptyResponseOptions,
    emptyResponseSectionCopy,
} from '@/features/datasets/utils/emptyResponsesHandling';

const { Text } = Typography;

/**
 * Lets the user choose how rows with an empty response cell are handled on
 * import. Options depend on the task:
 *
 * Multilabel (`isMultilabel === true`):
 * - "none_selected"     -> response.labels === [""] (default)
 * - "unlabeled"         -> empty response (default for non-multilabel)
 * - "generate_with_ai"  -> keep labeled rows; LABEL_ONLY fills empty ones
 * - "exclude"           -> skip the row
 *
 * Pure presentation: parent owns the value. When the incoming value is not
 * valid for the current mode it is normalized to the mode's default via
 * `onChange`.
 *
 * @param {Object} props
 * @param {'none_selected'|'unlabeled'|'generate_with_ai'|'exclude'} props.value
 * @param {(value: string) => void} props.onChange
 * @param {boolean} [props.isMultilabel=false]
 * @param {string} [props.taskType] TaskType value for copy nouns
 * @param {boolean} [props.includeGenerateWithAi=true]
 * @param {Object} [props.style]
 */

export {
    EMPTY_RESPONSES_NONE_SELECTED,
    EMPTY_RESPONSES_UNLABELED,
    EMPTY_RESPONSES_GENERATE_WITH_AI,
    EMPTY_RESPONSES_EXCLUDE,
};

const EmptyResponsesChoice = ({
    value,
    onChange,
    isMultilabel = false,
    taskType,
    includeGenerateWithAi = true,
    style,
}) => {
    const options = emptyResponseOptions({
        isMultilabel,
        taskType,
        includeGenerateWithAi,
    });
    const allowedValues = options.map((opt) => opt.value);
    const defaultValue = emptyResponseDefault(isMultilabel);
    const selectedValue = allowedValues.includes(value) ? value : defaultValue;
    const copy = emptyResponseSectionCopy(taskType);

    useEffect(() => {
        if (!allowedValues.includes(value)) {
            onChange(defaultValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isMultilabel, taskType]);

    return (
        <div
            style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #f0f0f0',
                ...style,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <Text style={{ fontWeight: 600, color: '#222', fontSize: 14, margin: 0 }}>
                    {copy.title}
                </Text>
                <Tooltip title={copy.tooltip}>
                    <QuestionCircleOutlined style={{ color: '#878787', fontSize: 14, cursor: 'help' }} />
                </Tooltip>
            </div>
            <Text
                style={{
                    display: 'block',
                    color: 'rgba(0, 0, 0, 0.45)',
                    fontSize: 13,
                    marginBottom: 12,
                }}
            >
                {copy.description}
            </Text>
            <Radio.Group
                value={selectedValue}
                onChange={(e) => onChange(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}
            >
                {options.map((opt) => (
                    <div key={opt.value} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Radio value={opt.value} style={{ fontSize: 14, color: '#222' }}>
                            {opt.title}
                        </Radio>
                        <Text
                            style={{
                                paddingLeft: 24,
                                color: 'rgba(0, 0, 0, 0.45)',
                                fontSize: 12,
                                lineHeight: 'normal',
                            }}
                        >
                            {opt.description}
                        </Text>
                    </div>
                ))}
            </Radio.Group>
        </div>
    );
};

export default EmptyResponsesChoice;
